// A formula in LaTeX, typeset with KaTeX (loaded only when a formula is first shown, as Plotly is). Until KaTeX has
// loaded - or if it cannot - the formula is shown as readable Unicode text (prettyFormula).
import { Box, Text } from "@mantine/core";
import { useEffect, useState } from "react";
import { prettyFormula } from "../lib/properties";

type Katex = { renderToString: (tex: string, opts: Record<string, unknown>) => string };
let katex: Promise<Katex> | null = null;
export function loadKatex(): Promise<Katex> {
  katex ??= Promise.all([import("katex"), import("katex/dist/katex.min.css")]).then(([m]) => ((m as any).default ?? m) as Katex);
  return katex;
}

/** KaTeX HTML for ``tex`` (no thrown errors: a bad formula shows KaTeX's red source text). */
export function renderFormula(k: Katex, tex: string, display = true): string {
  return k.renderToString(tex, { displayMode: display, throwOnError: false, output: "htmlAndMathml", strict: "ignore" });
}

export function Formula({ tex, display = true, testId }: { tex: string; display?: boolean; testId?: string }) {
  const [html, setHtml] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    setHtml(null);
    loadKatex().then((k) => { if (alive) setHtml(renderFormula(k, tex, display)); }).catch(() => undefined);
    return () => { alive = false; };
  }, [tex, display]);
  if (!html) return <Text size="sm" ff="serif" data-testid={testId} title={tex}>{prettyFormula(tex)}</Text>;
  // KaTeX's output is built from the calculator's own formula (trust off: no links, no raw HTML)
  return <Box data-testid={testId} title={tex} style={{ overflowX: "auto", overflowY: "hidden" }} fz="sm"
    dangerouslySetInnerHTML={{ __html: html }} />;
}
